import { Stack, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  applyNodeChanges,
  type Edge,
  type Node,
  type NodeChange,
  type NodeMouseHandler,
} from 'reactflow';
import { Loader, NamedLayoutBlock } from 'src/components';
import {
  getWorkflowTriggerType,
  usePrefetchWorkflowAssignments,
  useWorkflowVersions,
} from 'src/graphql/entities/workflow';
import type { WorkflowTriggerType } from 'src/graphql/entities/workflow/workflow.trigger';
import {
  WorkflowSelectionToolbar,
  WorkflowVersionActionsMenu,
} from 'src/pages/Settings/workflows/components';
import { useWorkflowRouteRedirects } from 'src/pages/Settings/workflows/use-workflow-route-redirects';
import { useWorkflowsWithPlatform } from 'src/pages/Settings/workflows/use-workflows-with-platform';
import {
  buildReactFlowGraph,
  parseWorkflowDefinition,
  WorkflowLayoutDirection,
  type WorkflowNodeDisplayData,
} from 'src/pages/Settings/workflows/utils/workflow-helpers';
import {
  DefinitionContent,
  VersionEmptyState,
  WorkflowDetailCard,
  WorkflowDetailContentContainer,
  WorkflowDetailsHeader,
  WorkflowEmptyState,
  WorkflowSelectionEmptyState,
} from 'src/pages/Settings/workflows/workflows-page.elements';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { useWorkflowsStore } from 'src/store/workflows';
import { useNavigateRoute } from 'src/utils/routing/use-navigate-route';

type RouteParamName<T extends string> = T extends `:${infer Name}` ? Name : never;

type WorkflowRouteParams = Partial<
  Record<
    RouteParamName<typeof UrlParams.workflowId | typeof UrlParams.workflowVersionId>,
    string
  >
>;

dayjs.extend(relativeTime);

export const WorkflowsPage = () => {
  const theme = useTheme();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showModal: showCreateDraftWorkflowVersionModal } = useModalMethods(
    Modals.CreateDraftWorkflowVersion,
  );
  const { showModal: showWorkflowAssignmentsModal } = useModalMethods(
    Modals.WorkflowAssignments,
  );
  const { showModal: showNewWorkflowModal } = useModalMethods(Modals.NewWorkflow);
  const { workflowId: workflowIdParam, workflowVersionId: workflowVersionIdParam } =
    useParams<WorkflowRouteParams>();
  const selectedWorkflowId = workflowIdParam || undefined;
  const selectedVersionId = workflowVersionIdParam || undefined;
  const [showRawDefinition, setShowRawDefinition] = useState(false);
  const [diagramNodes, setDiagramNodes] = useState<Node<WorkflowNodeDisplayData>[]>([]);
  const [diagramEdges, setDiagramEdges] = useState<Edge[]>([]);
  const [diagramLoading, setDiagramLoading] = useState(false);
  const [layoutDirection, setLayoutDirection] = useState<WorkflowLayoutDirection>(
    WorkflowLayoutDirection.Down,
  );
  const { navigate } = useNavigateRoute();
  const setLastVisited = useWorkflowsStore.use.setLastVisited();

  const {
    workflows: listedWorkflows,
    loading: workflowsLoading,
    refetchWorkflows,
  } = useWorkflowsWithPlatform(selectedPartnerId);

  // Partner 0's workflows are its own, so `isPlatform` is only ever set for another partner. A
  // workflow just created is the partner's own, so the listed rows are enough to decide this.
  const isViewOnly = listedWorkflows.some(
    (workflow) => workflow.workflowId === selectedWorkflowId && workflow.isPlatform,
  );

  // Warms the assignments modal's queries so it opens populated rather than empty. Only the
  // open modal is mounted, so it cannot do this for itself.
  usePrefetchWorkflowAssignments(selectedPartnerId);

  const {
    workflowVersions: listedWorkflowVersions,
    loading: versionsLoading,
    error: versionsError,
    refetchWorkflowVersions,
  } = useWorkflowVersions({
    workflowId: selectedWorkflowId,
  });

  const navigateToWorkflow = useCallback(
    (workflowId?: string, versionId?: string, options?: { replace?: boolean }) => {
      navigate(portalPaths.settings.workflows(workflowId, versionId), options);
    },
    [navigate],
  );

  // The lists as the page shows them: what was read, plus a just-created row the replica-backed
  // reads have not returned yet. See useWorkflowRouteRedirects.
  const { workflows, workflowVersions, holdSelection, releaseSelection } =
    useWorkflowRouteRedirects({
      selectedWorkflowId,
      selectedVersionId,
      workflows: listedWorkflows,
      workflowsLoading,
      workflowVersions: listedWorkflowVersions,
      versionsLoading,
      navigateToWorkflow,
      refetchWorkflows,
      refetchWorkflowVersions,
    });

  const selectedWorkflow = useMemo(
    () => workflows.find((workflow) => workflow.workflowId === selectedWorkflowId),
    [workflows, selectedWorkflowId],
  );
  const ownWorkflows = useMemo(
    () => workflows.filter((workflow) => !workflow.isPlatform),
    [workflows],
  );

  useEffect(() => {
    if (selectedWorkflowId && selectedVersionId) {
      setLastVisited(selectedWorkflowId, selectedVersionId);
    }
  }, [selectedWorkflowId, selectedVersionId, setLastVisited]);

  const selectedVersion = useMemo(
    () =>
      workflowVersions.find(
        (workflowVersion) => workflowVersion.workflowVersionId === selectedVersionId,
      ),
    [workflowVersions, selectedVersionId],
  );

  const handleWorkflowSelection = useCallback(
    (workflowId?: string) => {
      releaseSelection();
      navigateToWorkflow(workflowId, undefined);
    },
    [navigateToWorkflow, releaseSelection],
  );

  const handleVersionSelection = useCallback(
    (versionId?: string) => {
      releaseSelection();
      if (!selectedWorkflowId) {
        navigateToWorkflow(undefined, undefined);
        return;
      }

      navigateToWorkflow(selectedWorkflowId, versionId);
    },
    [navigateToWorkflow, releaseSelection, selectedWorkflowId],
  );

  const handleCreateDraft = useCallback(() => {
    showCreateDraftWorkflowVersionModal({
      workflowId: isViewOnly ? undefined : selectedWorkflowId,
      initialDefinition: selectedVersion?.definition,
      workflows: ownWorkflows,
      refetchWorkflowVersions,
      onCreated: (result) => {
        holdSelection({ workflowVersionId: result.workflowVersionId });
        navigateToWorkflow(result.workflowId, result.workflowVersionId);
      },
    });
  }, [
    holdSelection,
    isViewOnly,
    navigateToWorkflow,
    ownWorkflows,
    refetchWorkflowVersions,
    selectedVersion?.definition,
    selectedWorkflowId,
    showCreateDraftWorkflowVersionModal,
  ]);

  const openNewWorkflow = useCallback(
    (prefill: {
      initialTriggerType?: WorkflowTriggerType;
      initialSourceWorkflowId?: string;
    }) => {
      showNewWorkflowModal({
        ...prefill,
        // The rows come back with the create, so the page shows them at once rather than
        // waiting on lists that read a lagging replica. See useWorkflowRouteRedirects.
        onCreated: (result) => {
          holdSelection({
            workflow: { ...result.workflow, isPlatform: false },
            workflowVersion: result.workflowVersion,
          });
          navigateToWorkflow(result.workflowId, result.workflowVersionId);
        },
      });
    },
    [holdSelection, navigateToWorkflow, showNewWorkflowModal],
  );

  const activeVersion = useMemo(
    () => workflowVersions.find((workflowVersion) => workflowVersion.isActive),
    [workflowVersions],
  );
  const activeTriggerType = useMemo(
    () => getWorkflowTriggerType(activeVersion?.definition),
    [activeVersion?.definition],
  );

  const handleCopyToNewWorkflow = useCallback(() => {
    if (!selectedWorkflowId || !activeTriggerType) {
      return;
    }

    openNewWorkflow({
      initialTriggerType: activeTriggerType,
      initialSourceWorkflowId: selectedWorkflowId,
    });
  }, [activeTriggerType, openNewWorkflow, selectedWorkflowId]);

  const parsedDefinition = useMemo(
    () => parseWorkflowDefinition(selectedVersion?.definition),
    [selectedVersion?.definition],
  );
  const definitionError = parsedDefinition.error;

  useEffect(() => {
    let isActive = true;

    const definition = parsedDefinition.definition;
    if (!definition) {
      setDiagramNodes([]);
      setDiagramEdges([]);
      setDiagramLoading(false);
      return;
    }

    setDiagramLoading(true);
    const runLayout = async () => {
      try {
        const graph = await buildReactFlowGraph(
          definition,
          theme.palette.mode === 'dark',
          layoutDirection,
        );
        if (!isActive) {
          return;
        }

        setDiagramNodes(graph.nodes);
        setDiagramEdges(graph.edges);
      } catch (error) {
        console.error('Failed to build workflow graph', error);
        if (isActive) {
          setDiagramNodes([]);
          setDiagramEdges([]);
        }
      } finally {
        if (isActive) {
          setDiagramLoading(false);
        }
      }
    };

    void runLayout();

    return () => {
      isActive = false;
    };
  }, [layoutDirection, parsedDefinition.definition, theme.palette.mode]);

  const formattedDefinitionText = useMemo(() => {
    if (!selectedVersion?.definition) {
      return 'No definition available.';
    }

    try {
      return JSON.stringify(JSON.parse(selectedVersion.definition), null, 2);
    } catch {
      return selectedVersion.definition;
    }
  }, [selectedVersion?.definition]);

  const noWorkflows = !workflowsLoading && workflows.length === 0;
  const noVersions =
    !versionsLoading && selectedWorkflowId && workflowVersions.length === 0 && !versionsError;
  const initialLoading = workflowsLoading && workflows.length === 0;

  const workflowSelectDisabled = workflowsLoading || workflows.length === 0;
  const versionSelectDisabled =
    workflowSelectDisabled || versionsLoading || workflowVersions.length === 0;

  const isShowingRawDefinition = showRawDefinition || !parsedDefinition.definition;
  const showDefinitionLoader = versionsLoading && !!selectedWorkflowId && !selectedVersion;
  const showDiagramLoader = diagramLoading && !isShowingRawDefinition;
  const pendingDiagram =
    showDiagramLoader ||
    (!isShowingRawDefinition &&
      !diagramNodes.length &&
      !!parsedDefinition.definition &&
      !definitionError);
  const hasDiagramNodes = diagramNodes.length > 0;

  useEffect(() => {
    if (definitionError) {
      setShowRawDefinition(true);
      return;
    }

    setShowRawDefinition(false);
  }, [definitionError, selectedVersionId]);

  const handleDiagramNodeClick = useCallback<NodeMouseHandler>((_, node) => {
    setDiagramNodes((prev) => {
      const maxZ = prev.reduce((max, current) => Math.max(max, current.zIndex ?? 0), 0);
      const nextZ = maxZ + 1;
      return prev.map((item) =>
        item.id === node.id
          ? {
              ...item,
              zIndex: nextZ,
            }
          : item,
      );
    });
  }, []);

  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    setDiagramNodes((prev) => applyNodeChanges(changes, prev));
  }, []);

  const handleLayoutChange = useCallback((direction: WorkflowLayoutDirection) => {
    setLayoutDirection(direction);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    releaseSelection();
    if (!selectedWorkflowId) {
      navigateToWorkflow(undefined, undefined, { replace: true });
      return;
    }

    navigateToWorkflow(selectedWorkflowId, undefined, { replace: true });
  }, [navigateToWorkflow, releaseSelection, selectedWorkflowId]);

  const toolbarBorderColor =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.12)
      : theme.palette.divider;

  const versionActions =
    selectedWorkflow && selectedVersion ? (
      <WorkflowVersionActionsMenu
        partnerId={selectedPartnerId}
        workflowName={selectedWorkflow.name}
        selectedVersion={selectedVersion}
        workflowVersions={workflowVersions}
        refetchWorkflowVersions={refetchWorkflowVersions}
        onDeleteSuccess={handleDeleteSuccess}
        viewOnly={isViewOnly}
      />
    ) : null;

  let primaryContent: ReactNode = null;

  if (initialLoading) {
    primaryContent = (
      <WorkflowDetailCard>
        <Stack alignItems="center" justifyContent="center" height="100%" minHeight={320}>
          <Loader />
        </Stack>
      </WorkflowDetailCard>
    );
  } else if (noWorkflows) {
    primaryContent = <WorkflowEmptyState />;
  } else if (noVersions) {
    primaryContent = (
      <WorkflowDetailCard>
        <VersionEmptyState workflowName={selectedWorkflow?.name} />
      </WorkflowDetailCard>
    );
  } else if (showDefinitionLoader) {
    primaryContent = (
      <WorkflowDetailCard>
        <Stack alignItems="center" justifyContent="center" height="100%" minHeight={320}>
          <Loader />
        </Stack>
      </WorkflowDetailCard>
    );
  } else if (selectedWorkflow && selectedVersion) {
    const definitionContent = (
      <DefinitionContent
        pendingDiagram={pendingDiagram}
        hasDiagramNodes={hasDiagramNodes}
        showRawDefinition={isShowingRawDefinition}
        diagramNodes={diagramNodes}
        diagramEdges={diagramEdges}
        layoutDirection={layoutDirection}
        definitionError={definitionError || undefined}
        formattedDefinitionText={formattedDefinitionText}
        onDiagramNodeClick={handleDiagramNodeClick}
        onNodesChange={handleNodesChange}
        onLayoutChange={handleLayoutChange}
      />
    );

    primaryContent = (
      <WorkflowDetailCard>
        <WorkflowDetailsHeader
          workflowName={selectedWorkflow.name}
          versionNumber={selectedVersion.version}
          statusLabel={selectedVersion.isActive ? 'Active' : 'Draft'}
          updatedAtLabel={dayjs(selectedVersion.updatedAt).fromNow()}
          showRawDefinition={isShowingRawDefinition}
          onToggleDefinition={() => {
            if (definitionError || !parsedDefinition.definition) {
              return;
            }

            setShowRawDefinition((prev) => !prev);
          }}
          disableToggle={!!definitionError || !parsedDefinition.definition}
          viewOnly={isViewOnly}
          onCopyToNewWorkflow={handleCopyToNewWorkflow}
          copyToNewWorkflowDisabled={!activeTriggerType}
        />
        <WorkflowDetailContentContainer>{definitionContent}</WorkflowDetailContentContainer>
      </WorkflowDetailCard>
    );
  } else if (!versionsLoading) {
    primaryContent = (
      <WorkflowDetailCard>
        <WorkflowSelectionEmptyState />
      </WorkflowDetailCard>
    );
  }

  return (
    <NamedLayoutBlock
      title="Workflows"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      BlockContentProps={{
        sx: { flex: 1, p: 0 },
      }}
    >
      <Stack spacing={0} sx={{ flex: 1, minHeight: 0 }}>
        <Stack
          spacing={1}
          sx={{
            pb: 2,
            borderBottom: `1px solid ${toolbarBorderColor}`,
            p: theme.spacing(2),
          }}
        >
          <WorkflowSelectionToolbar
            workflows={workflows}
            workflowVersions={workflowVersions}
            selectedWorkflowId={selectedWorkflowId}
            selectedVersionId={selectedVersionId}
            workflowSelectDisabled={workflowSelectDisabled}
            versionSelectDisabled={versionSelectDisabled}
            newDraftVersionDisabled={isViewOnly || ownWorkflows.length === 0}
            onWorkflowChange={handleWorkflowSelection}
            onVersionChange={handleVersionSelection}
            onNewWorkflow={() => openNewWorkflow({})}
            onNewDraftVersion={handleCreateDraft}
            onOpenAssignments={() => showWorkflowAssignmentsModal(null)}
            versionActions={versionActions}
          />
        </Stack>

        <Stack sx={{ flex: 1, minHeight: 0, py: 2 }}>{primaryContent}</Stack>
      </Stack>
    </NamedLayoutBlock>
  );
};
